import { Skeleton } from "@foreday/ui/components/skeleton";
import {
  type QueryClient,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import type * as React from "react";
import { Suspense, useState } from "react";
import { ConfirmIdentityDialog } from "@/client/components/confirm-identity";
import { Page } from "@/client/components/page";
import {
  Delayed,
  HeldButton,
  HeldTitle,
  PagePending,
  ShapeSkeleton,
} from "@/client/components/page-pending";
import { PersonWork } from "@/client/components/person-work";
import { ListSkeleton } from "@/client/components/route-states";
import { TableSkeleton } from "@/client/components/table-skeleton";
import { dayOf, formatShortWeekdayOf } from "@/client/lib/dates";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { holidaysQuery, scheduleQuery } from "@/client/lib/schedule-query";
import { monthForPerson, payTab, personLeave } from "@/client/lib/screen-parts";
import { ensureTenantData } from "@/client/lib/session";
import { tasksQuery } from "@/client/lib/tasks-query";
import { openCounts } from "@/client/lib/team-tasks";
import { useNow } from "@/client/lib/use-now";
import {
  monthQuery,
  myTodayQuery,
  openSince,
  todayQuery,
} from "@/features/attendance/attendance-query";
import { MonthStepper } from "@/features/attendance/components/month-stepper";
import { attendanceCopy } from "@/features/attendance/copy";
import { changesHoursOf } from "@/features/attendance/roles";
import { dayIn, monthIn } from "@/features/attendance/rules";
import { myMonthSearch } from "@/features/attendance/schemas";
import { LeaveBalances } from "@/features/leave/components/leave-balances";
import {
  ensureMonthLeave,
  lastDayAway,
  leaveBalancesQuery,
  leaveOffQuery,
  leaveSetupQuery,
  leaveTypesQuery,
  monthLeaveQuery,
  personLeaveQuery,
} from "@/features/leave/leave-query";
import { useMonthLeave } from "@/features/leave/use-month-leave";
import type { PayTab as PayTabComponent } from "@/features/payslips/components/pay-tab";
import { payHistoryQuery } from "@/features/payslips/payslips-query";
import { PersonActions } from "@/features/people/components/person-actions";
import {
  PersonHeader,
  PersonHeaderPending,
} from "@/features/people/components/person-header";
import {
  PersonNotFound,
  PersonTabRow,
  PersonTabs,
  RemovedBanner,
} from "@/features/people/components/person-page";
import { peopleCopy } from "@/features/people/copy";
import {
  ensurePerson,
  peopleQuery,
  personQuery,
} from "@/features/people/people-query";
import {
  mayChangeAccessOf,
  personViewsFor,
  runsTeam,
} from "@/features/people/roles";
import {
  PERSON_SEARCH_DEFAULTS,
  type PersonDetail,
  type PersonRow,
  type PersonView,
  personSearch,
} from "@/features/people/schemas";
import { usePersonAccess } from "@/features/people/use-person-access";
import {
  preferencesQuery,
  profileQuery,
} from "@/features/settings/settings-query";
import { projectsQuery } from "@/features/tasks/projects-query";
import type { ListedTask } from "@/features/tasks/schemas";
import { leaveYearOf } from "@/shared/leave-year";
import { mayDecide, type PersonRole } from "@/shared/roles";

const TICK = 60_000;

/** What the Attendance tab draws on its first frame: the month, its calendar and the leave in it. */
async function ensureMonth(
  context: {
    readonly queryClient: QueryClient;
    readonly companyTimezone: string;
    readonly companyModules: readonly string[];
  },
  personId: string,
  asked: string | undefined,
) {
  if (!context.companyModules.includes("attendance")) return;
  const now = new Date();
  const month = asked ?? monthIn(context.companyTimezone, now);
  const year = Number(month.slice(0, 4));
  await Promise.all([
    monthForPerson.load(),
    ensureTenantData(context.queryClient, monthQuery(month)),
    ensureTenantData(context.queryClient, scheduleQuery),
    ensureTenantData(context.queryClient, holidaysQuery(year)),
    ensureTenantData(
      context.queryClient,
      myTodayQuery(dayIn(context.companyTimezone, now)),
    ),
    context.companyModules.includes("leave") &&
      ensureMonthLeave(context.queryClient, personId, month),
  ]);
}

/** What the Leave tab draws on its first frame: the balances and this year's and next year's asks. */
async function ensureLeave(
  context: {
    readonly queryClient: QueryClient;
    readonly companyTimezone: string;
  },
  personId: string,
) {
  const { queryClient } = context;
  const [, , { startMonth }] = await Promise.all([
    personLeave.load(),
    ensureTenantData(queryClient, preferencesQuery),
    ensureTenantData(queryClient, leaveSetupQuery),
    ensureTenantData(queryClient, leaveBalancesQuery(personId)),
  ]);
  const today = dayIn(context.companyTimezone, new Date());
  await ensureTenantData(
    queryClient,
    personLeaveQuery(personId, leaveYearOf(today, startMonth)),
  );
}

export const Route = createFileRoute("/_authenticated/$slug/team_/$personId")({
  validateSearch: personSearch.extend(myMonthSearch.shape),
  search: { middlewares: [stripSearchParams(PERSON_SEARCH_DEFAULTS)] },
  loaderDeps: ({ search }) => ({ view: search.view, month: search.month }),
  loader: async ({ context, params, deps }) => {
    // Whether this person is the last admin is answered from the whole list, which keeps it out
    // of the person's own response.
    const [, found] = await Promise.all([
      ensureTenantData(context.queryClient, peopleQuery),
      ensurePerson(context.queryClient, params.personId),
    ]);
    if (!found) return { found };
    const presence = runsTeam(context.role);
    const modules = context.companyModules;
    const views = async () => {
      if (
        deps.view === "pay" &&
        context.role === "admin" &&
        modules.includes("payslips")
      ) {
        await Promise.all([
          payTab.load(),
          ensureTenantData(
            context.queryClient,
            payHistoryQuery(params.personId),
          ),
        ]);
      }
      if (deps.view !== "attendance" && deps.view !== "leave") return;
      const { personId: viewerId } = await ensureTenantData(
        context.queryClient,
        profileQuery,
      );
      // A tab the viewer may not open reads as Tasks, and another's leave would be refused.
      const open = personViewsFor(
        context.role,
        viewerId === params.personId,
        modules,
      );
      if (deps.view === "attendance" && open.includes("attendance")) {
        await ensureMonth(context, params.personId, deps.month);
      }
      if (deps.view === "leave" && open.includes("leave")) {
        await ensureLeave(context, params.personId);
      }
    };
    // A tab's data is asked for here and not awaited, so a tab or a month changes under a header,
    // tabs and stepper that stay mounted; the tab's body waits under its own Suspense, and a read
    // that failed here is refused there.
    void views().catch(() => undefined);
    await Promise.all([
      presence && modules.includes("attendance")
        ? ensureTenantData(
            context.queryClient,
            todayQuery(dayIn(context.companyTimezone, new Date())),
          )
        : null,
      presence && modules.includes("leave")
        ? ensureTenantData(context.queryClient, leaveOffQuery)
        : null,
      // The remove dialog counts open tasks too, so the working set is read on every tab.
      ensureTenantData(context.queryClient, tasksQuery),
      ensureTenantData(context.queryClient, projectsQuery),
    ]);
    return { found };
  },
  head: async ({ match }) => {
    const { peopleCopy } = await import("@/features/people/copy");
    return {
      meta: [
        {
          title: pageTitle(
            match.loaderData?.found
              ? match.context.queryClient.getQueryData(
                  personQuery(match.params.personId).queryKey,
                )?.name
              : peopleCopy.person.notFoundTitle,
            match.context.companyName,
          ),
        },
      ],
    };
  },
  pendingMs: PENDING_MS,
  pendingComponent: PendingPerson,
  component: Person,
});

function Person() {
  const { slug } = Route.useParams();
  const { found } = Route.useLoaderData();
  return found ? <Found /> : <PersonNotFound slug={slug} />;
}

/**
 * The open tab: one the viewer may not open reads as Tasks rather than an empty slot. Someone not
 * read yet may be the viewer, whose own page has every tab, so the address's tab holds.
 */
function useTabs(email: string | undefined) {
  const { slug, personId } = Route.useParams();
  const { role, email: viewer, companyModules } = Route.useRouteContext();
  const search = Route.useSearch();
  const views = personViewsFor(
    role,
    email === undefined || email === viewer,
    companyModules,
  );
  const view: PersonView = views.includes(search.view) ? search.view : "tasks";
  const tabs =
    views.length > 1 ? (
      <PersonTabs
        slug={slug}
        personId={personId}
        views={views}
        search={search}
      />
    ) : null;
  return { view, tabs };
}

function Found() {
  const { personId } = Route.useParams();
  const { role, email, companyTimezone } = Route.useRouteContext();
  const now = useNow(TICK);
  const { data: person } = useSuspenseQuery(personQuery(personId));
  const { data: people } = useSuspenseQuery(peopleQuery);
  const { data: tasks } = useSuspenseQuery(tasksQuery);
  const { view, tabs } = useTabs(person.email);
  const today = dayOf(now, companyTimezone);
  const meId =
    people.find((row) => row.kind === "person" && row.email === email)?.id ??
    null;

  return (
    <Page shape="document">
      <PersonTop person={person} people={people} tasks={tasks} now={now} />
      {view === "tasks" ? (
        <PersonWork
          person={person}
          tabs={tabs}
          today={today}
          meId={meId}
          mayReassign={runsTeam(role)}
        />
      ) : (
        <>
          <PersonTabRow
            tabs={tabs}
            aside={
              view === "attendance" ? (
                <AttendanceStepper now={now} />
              ) : view === "leave" ? (
                <Suspense
                  fallback={
                    <Delayed>
                      <HeldTitle className="text-body" lines={["w-48"]} />
                    </Delayed>
                  }
                >
                  <TheirBalances personId={personId} />
                </Suspense>
              ) : null
            }
          />
          <section data-view={view} aria-label={peopleCopy.person.tabs[view]}>
            <Suspense
              fallback={
                <Delayed>
                  <ShapeSkeleton shape="list" />
                </Delayed>
              }
            >
              {view === "attendance" ? (
                <PersonAttendance personId={personId} now={now} />
              ) : null}
              {view === "leave" ? <TheirLeave person={person} /> : null}
              {view === "pay" ? (
                <PayTabScreen
                  personId={personId}
                  name={person.name}
                  today={dayIn(companyTimezone, now)}
                  timeZone={companyTimezone}
                />
              ) : null}
            </Suspense>
          </section>
        </>
      )}
    </Page>
  );
}

/**
 * A person's first load, and a tab's or a month's: the tabs for real, and the header for real too
 * once the person was read, so switching a tab redraws only what is under the tabs.
 */
function PendingPerson() {
  const { slug, personId } = Route.useParams();
  const { role } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const now = new Date();
  const person = queryClient.getQueryData(personQuery(personId).queryKey);
  const people = queryClient.getQueryData(peopleQuery.queryKey);
  const tasks = queryClient.getQueryData(tasksQuery.queryKey);
  const profile = queryClient.getQueryData(profileQuery.queryKey);
  const someone = people?.find((row) => row.id === personId);
  const { view, tabs } = useTabs((person ?? someone)?.email);
  return (
    <PagePending
      shape="document"
      header={
        <>
          {person && people && tasks ? (
            <PersonTop
              person={person}
              people={people}
              tasks={tasks}
              now={now}
            />
          ) : (
            <PersonHeaderPending
              slug={slug}
              someone={someone}
              menu={
                role === "admin" && (someone?.kind ?? "person") === "person"
              }
            />
          )}
          <PersonTabRow
            tabs={tabs}
            aside={
              view === "attendance" ? (
                <AttendanceStepper now={now} />
              ) : view === "leave" || (view === "tasks" && runsTeam(role)) ? (
                <HeldTitle className="text-body" lines={["w-48"]} />
              ) : null
            }
          />
        </>
      }
    >
      {view === "attendance" ? (
        <MonthForPersonSkeleton
          changes={
            profile && people
              ? changesHoursOf(
                  { personId: profile.personId, role },
                  personId,
                  people,
                )
              : runsTeam(role)
          }
        />
      ) : (
        <ListSkeleton lines={2} />
      )}
    </PagePending>
  );
}

/** The header, its menu, and the banner of someone removed: the same on every tab. */
function PersonTop({
  person,
  people,
  tasks,
  now,
}: {
  readonly person: PersonDetail;
  readonly people: readonly PersonRow[];
  readonly tasks: readonly ListedTask[];
  readonly now: Date;
}) {
  const { slug, personId } = Route.useParams();
  const { role, email, companyTimezone, companyName, companyModules } =
    Route.useRouteContext();
  const queryClient = useQueryClient();
  const presence = runsTeam(role) && companyModules.includes("attendance");
  const inSince = presence
    ? openSince(
        queryClient
          .getQueryData(todayQuery(dayIn(companyTimezone, now)).queryKey)
          ?.find((row) => row.personId === personId),
      )
    : null;
  const awayTo =
    runsTeam(role) && companyModules.includes("leave")
      ? lastDayAway(
          queryClient.getQueryData(leaveOffQuery.queryKey) ?? [],
          personId,
          dayIn(companyTimezone, now),
        )
      : null;
  const admin = mayChangeAccessOf({ role, email: email ?? "" }, person, people);
  const access = usePersonAccess(personId, () => undefined);
  const today = dayOf(now, companyTimezone);

  return (
    <>
      <PersonHeader
        slug={slug}
        person={person}
        timeZone={companyTimezone}
        now={now}
        showsPresence={runsTeam(role)}
        inSince={inSince}
        awayTo={awayTo ? formatShortWeekdayOf(awayTo) : null}
        menu={
          admin || (role === "admin" && person.kind === "person") ? (
            <PersonActions
              person={person}
              changesAccess={admin}
              company={companyName ?? ""}
              link={`${location.origin}/${slug}/team/${personId}`}
              openTasks={openCounts(tasks, today).get(personId)?.open ?? 0}
              start={{
                startOn:
                  person.startOn ??
                  (person.joinedAt
                    ? dayOf(new Date(person.joinedAt), companyTimezone)
                    : today),
                today,
                timeZone: companyTimezone,
              }}
            />
          ) : null
        }
      />
      {person.removedAt ? (
        <RemovedBanner
          removedAt={person.removedAt}
          removedByName={person.removedByName}
          timeZone={companyTimezone}
          busy={access.busy}
          onReactivate={
            role === "admin"
              ? () => access.change({ intent: "reactivate" })
              : undefined
          }
        />
      ) : null}
      <ConfirmIdentityDialog
        ask={access.ask}
        busy={access.busy}
        onConfirm={access.confirm}
        onCancel={access.reset}
      />
    </>
  );
}

function AttendanceStepper({ now }: { readonly now: Date }) {
  const search = Route.useSearch();
  const { companyTimezone } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  return (
    <MonthStepper
      month={search.month ?? monthIn(companyTimezone, now)}
      // Replaced so Back leaves the person instead of stepping through every month.
      onMonth={(next) =>
        navigate({ search: { ...search, month: next }, replace: true })
      }
    />
  );
}

/**
 * The month's first load: its line of totals, then the days in `PersonMonth`'s own columns (Day,
 * then In, Out and Hours) and row height. Here rather than there, which loads with the month.
 * Narrower than the panel's middle width the totals wrap, and each day carries its times under
 * the date, with Change times for someone who may change them.
 */
function MonthForPersonSkeleton({ changes }: { readonly changes: boolean }) {
  return (
    <div className="flex flex-col gap-5">
      <HeldTitle
        className="text-body"
        lines={["w-72", "w-40 @panel-md/main:hidden"]}
      />
      <TableSkeleton
        row="h-[var(--row-h-day)]"
        columns={[
          {
            className: "@panel-md/main:w-0 @panel-md/main:pr-8",
            bar: "w-28",
            below: (
              <div className="flex flex-col gap-1 py-1 @panel-md/main:hidden">
                <div className="flex h-lh items-center text-body">
                  <Skeleton className="h-3 w-36" />
                </div>
                {changes ? (
                  <HeldButton size="sm" className="self-start">
                    {attendanceCopy.day.changeTimes}
                  </HeldButton>
                ) : null}
              </div>
            ),
          },
          {
            className: "hidden @panel-md/main:table-cell",
            bar: "hidden",
            untitled: true,
          },
          { className: "hidden w-32 @panel-md/main:table-cell", bar: "w-14" },
          { className: "hidden w-32 @panel-md/main:table-cell", bar: "w-14" },
          { className: "@panel-md/main:w-32", bar: "w-12", end: true },
        ]}
      />
    </div>
  );
}

function PersonAttendance({
  personId,
  now,
}: {
  readonly personId: string;
  readonly now: Date;
}) {
  const search = Route.useSearch();
  const { companyTimezone, companyModules } = Route.useRouteContext();
  const month = search.month ?? monthIn(companyTimezone, now);
  return companyModules.includes("leave") ? (
    <MonthLeaveRead personId={personId} month={month}>
      <PersonMonth personId={personId} month={month} now={now} />
    </MonthLeaveRead>
  ) : (
    <PersonMonth personId={personId} month={month} now={now} />
  );
}

/** Holds the tab until the month's leave is read, so the month draws with its leave, not before it. */
function MonthLeaveRead({
  personId,
  month,
  children,
}: {
  readonly personId: string;
  readonly month: string;
  readonly children: React.ReactNode;
}) {
  const {
    data: { startMonth },
  } = useSuspenseQuery(leaveSetupQuery);
  useSuspenseQuery(monthLeaveQuery(personId, month, startMonth));
  return children;
}

function PersonMonth({
  personId,
  month,
  now,
}: {
  readonly personId: string;
  readonly month: string;
  readonly now: Date;
}) {
  const { slug } = Route.useParams();
  const { role, companyTimezone, companyModules } = Route.useRouteContext();
  const { data: people } = useSuspenseQuery(peopleQuery);
  const { leave, leaveTypes } = useMonthLeave(
    personId,
    month,
    companyModules.includes("leave"),
  );
  const {
    data: { personId: viewerId },
  } = useSuspenseQuery(profileQuery);
  const own = viewerId === personId;
  const changes = changesHoursOf(
    { personId: viewerId, role },
    personId,
    people,
  );
  const [MonthForPerson] = useState(monthForPerson.pick);
  return (
    <MonthForPerson
      personId={personId}
      month={month}
      own={own}
      timeZone={companyTimezone}
      now={now}
      slug={slug}
      people={people}
      leave={leave}
      leaveTypes={leaveTypes}
      changedBy={changes ? viewerId : undefined}
    />
  );
}

function TheirBalances({ personId }: { readonly personId: string }) {
  const { data: types } = useSuspenseQuery(leaveTypesQuery);
  const { data: balances } = useSuspenseQuery(leaveBalancesQuery(personId));
  return <LeaveBalances types={types} balances={balances.items} />;
}

/** Their leave to come and their earlier leave; a Manager or an Admin who decides it acts on it. */
function TheirLeave({
  person,
}: {
  readonly person: {
    id: string;
    name: string;
    role: PersonRole;
    removedAt: string | null;
  };
}) {
  const { role, companyTimezone } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const now = useNow(TICK);
  const today = dayIn(companyTimezone, now);
  const { data: profile } = useSuspenseQuery(profileQuery);
  const { data: people } = useSuspenseQuery(peopleQuery);
  const { data: preferences } = useSuspenseQuery(preferencesQuery);
  const {
    data: { startMonth, items: types },
  } = useSuspenseQuery(leaveSetupQuery);
  const { data: leave } = useSuspenseQuery(
    personLeaveQuery(person.id, leaveYearOf(today, startMonth)),
  );
  const [PersonLeave] = useState(personLeave.pick);
  return (
    <PersonLeave
      person={person}
      today={today}
      timeZone={companyTimezone}
      startsOn={preferences.startOfWeek}
      viewerId={profile.personId}
      leave={leave}
      people={people}
      types={types}
      decides={
        profile.personId !== person.id &&
        person.removedAt === null &&
        mayDecide(role, person.role)
      }
      opened={search}
      onOpen={(next) =>
        navigate({
          search: {
            ...search,
            add: undefined,
            cancel: undefined,
            decide: undefined,
            ...next,
          },
        })
      }
    />
  );
}

function PayTabScreen(props: React.ComponentProps<typeof PayTabComponent>) {
  const [PayTab] = useState(payTab.pick);
  return <PayTab {...props} />;
}
